import { useCallback, useEffect, useRef, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type ArquivoInfo } from "../api";
import { IconClose, IconFolder, IconUpload, IconDownload, IconTrash, IconFile } from "../icons";
import { usePodeExcluir } from "../permissoes";

interface Props {
  colaborador: Row; // dono da pasta
  onClose: () => void;
}

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Pasta do colaborador: lista, envia, baixa e remove certificados/documentos. */
export default function PastaModal({ colaborador, onClose }: Props) {
  // Enviar e baixar seguem liberados; apagar documento é só de administrador.
  const podeExcluir = usePodeExcluir();
  const [arquivos, setArquivos] = useState<ArquivoInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setArquivos(await api.arquivos.list(colaborador.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao listar arquivos");
    } finally {
      setLoading(false);
    }
  }, [colaborador.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function enviar(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEnviando(true);
    setError(null);
    try {
      await api.arquivos.upload(colaborador.id, files);
      if (fileRef.current) fileRef.current.value = "";
      await carregar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao enviar arquivo");
    } finally {
      setEnviando(false);
    }
  }

  async function remover(nome: string) {
    if (!confirm(`Excluir "${nome}"?`)) return;
    try {
      await api.arquivos.remove(colaborador.id, nome);
      await carregar();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ia" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconFolder size={18} /> Pasta — {colaborador.nome}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="pasta-upload">
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => enviar(e.target.files)} />
            <button
              type="button"
              className="btn-primary"
              onClick={() => fileRef.current?.click()}
              disabled={enviando}
            >
              <IconUpload size={16} /> {enviando ? "Enviando…" : "Enviar arquivo"}
            </button>
            <span className="hint">Certificados, documentos e comprovantes do colaborador.</span>
          </div>

          {error && <div className="form-error">{error}</div>}

          {loading ? (
            <div className="empty">Carregando…</div>
          ) : arquivos.length === 0 ? (
            <div className="empty">Nenhum arquivo nesta pasta ainda.</div>
          ) : (
            <ul className="pasta-list">
              {arquivos.map((a) => (
                <li key={a.nome} className="pasta-item">
                  <IconFile size={18} />
                  <div className="pasta-info">
                    <a
                      href={api.arquivos.url(colaborador.id, a.nome)}
                      target="_blank"
                      rel="noreferrer"
                      className="pasta-nome"
                    >
                      {a.nome}
                    </a>
                    <span className="pasta-meta">
                      {tamanhoLegivel(a.tamanho)} • {new Date(a.enviado_em).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <a
                    className="icon-btn"
                    title="Baixar"
                    href={api.arquivos.url(colaborador.id, a.nome)}
                    download={a.nome}
                  >
                    <IconDownload size={17} />
                  </a>
                  {podeExcluir && (
                    <button className="icon-btn icon-btn-danger" title="Excluir" onClick={() => remover(a.nome)}>
                      <IconTrash size={17} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
